export const tenMegabytes = 10 * 1024 * 1024;

// Série fixa da NFS-e Nacional, definida e controlada pela Cakto. Espelha o
// NFSE_SERIE_NFSEN do cakto-split (nfse/constants.py) — o prestador não escolhe
// nem edita. Exibida apenas como informação no cadastro fiscal.
export const NFSE_SERIE = '1910';

// Passos do wizard de ativação de NFS-e, na ordem em que aparecem.
export const WIZARD_STEPS = [
  'Dados da empresa',
  'Regime Tributário',
  'Dados fiscais',
  'Certificado',
  'Simular NFSe',
];

// O passo de regime tributário oferece três escolhas, e cada uma fixa o par de
// campos que o backend espera. É a mesma correspondência que o initInvoiceForm
// já deriva do CNPJ (opcao_pelo_simples / opcao_mei).
export const TAX_REGIME_OPTIONS = [
  {
    value: 'mei',
    label: 'MEI',
    description:
      'Para microempreendedores individuais com faturamento de até R$ 81 mil ao ano.',
    taxRegime: '4',
    situationSimplesNacional: '2',
  },
  {
    value: 'simples',
    label: 'Simples Nacional',
    description:
      'Para micro e pequenas empresas com faturamento de até R$ 4,8 milhões ao ano.',
    taxRegime: '1',
    situationSimplesNacional: '3',
  },
  {
    value: 'lucro',
    label: 'Lucro Presumido ou Lucro Real',
    description: 'Para empresas com faturamento acima dos limites do Simples Nacional.',
    taxRegime: '3',
    situationSimplesNacional: '1',
  },
];

// Regime de apuração pelo Simples Nacional (regApTribSN da DPS). O ambiente
// nacional rejeita a nota com E0166 quando a empresa é optante e este campo não
// vem. Só aparece no wizard para MEI e Simples — fora do Simples não se aplica.
export const SN_APURACAO_OPTIONS = [
  { value: '1', label: 'Tributos federais e municipal apurados pelo Simples Nacional' },
  { value: '2', label: 'Federais pelo Simples Nacional e ISSQN por fora' },
  { value: '3', label: 'Federais e municipal por fora do Simples Nacional' },
];

// Os regimes cujo cadastro exige o regime de apuração acima. Espelha o
// codigo_opcao_simples_nacional de optante (MEI e ME/EPP) do backend.
export const TAX_REGIMES_OPTANTES_SN = ['4', '1'];

// Página da documentação técnica da reforma tributária na NFS-e, e não o
// arquivo de um anexo: os anexos saem em versões novas (o VII já foi para a
// v1.03.00 com a NT 009), e o link para uma versão fixa envelhece sem aviso.
// O Anexo VIII, que correlaciona CTN e NBS com cIndOp e cClassTrib, é publicado
// pelo governo como referência ainda em desenvolvimento; nem a Sefin nem o
// backend validam o par por ele.
export const DOCUMENTACAO_IBS_CBS_URL =
  'https://www.gov.br/nfse/pt-br/biblioteca/documentacao-tecnica/rtc';

// Rádio, e não checkbox: o campo começa sem resposta (`''`) e só o prestador
// marca Sim ou Não. Um checkbox nasceria desmarcado, e desmarcado já é "Não" —
// uma declaração fiscal que ele não fez.
//
// Fonte única dos valores: o `oneOf` do schema, a ida para o payload
// (`apiValue`) e a volta do cadastro salvo leem daqui, para uma opção nova não
// ficar aceita na tela e descartada no envio.
export const FINAL_CONSUMER_OPTIONS = [
  { value: 'true', label: 'Sim', apiValue: true },
  { value: 'false', label: 'Não', apiValue: false },
];

// Campos do grupo IBS/CBS do serviço no formulário. Obrigatórios: sem eles o
// Emissor Nacional recusa a nota de teste em qualquer regime.
export const IBS_CBS_FIELDS = ['operationIndicatorCode', 'taxClassificationCode', 'finalConsumer'];

// Rótulos do grupo, lidos pela tela e pelo mapa de erros do backend: o toast
// precisa chamar o campo pelo mesmo nome que o prestador vê.
export const IBS_CBS_LABELS = {
  operationIndicatorCode: 'Indicador da operação (cIndOp)',
  taxClassificationCode: 'Classificação tributária (cClassTrib)',
  finalConsumer: 'Consumidor final',
};

export const invoiceStatus = {
  authorized: 'authorized',
  processing: 'processing',
  canceled: 'canceled',
  authz_error: 'authz_error',
  // A nota substituída por outra; a lista única (CKTMF-536) passou a devolvê-la.
  replaced: 'replaced',
};

export const invoiceStatusEnabledView = [invoiceStatus.authorized, invoiceStatus.canceled];

export const invoiceStatusEnabledCancel = [invoiceStatus.authorized];

// Só a nota recusada pode ser reemitida: as outras já viraram nota na
// prefeitura, ou ainda podem virar.
export const invoiceStatusEnabledReemit = [invoiceStatus.authz_error];

// Períodos do filtro único da página: o mesmo intervalo vale para os cards, o
// gráfico e a tabela. `custom` não aparece na lista — é o que fica selecionado
// quando o usuário mexe nas datas na mão.
export const DASHBOARD_PERIOD_PRESETS = {
  last7Days: 'last7Days',
  last30Days: 'last30Days',
  last90Days: 'last90Days',
  last365Days: 'last365Days',
  all: 'all',
  custom: 'custom',
};

// `days` conta o dia de hoje: "7 dias" é hoje e os seis anteriores.
// `null` é o intervalo em aberto, que o backend resolve da primeira nota.
export const DASHBOARD_PERIOD_OPTIONS = [
  { value: DASHBOARD_PERIOD_PRESETS.last7Days, label: '7 dias', days: 7 },
  { value: DASHBOARD_PERIOD_PRESETS.last30Days, label: '30 dias', days: 30 },
  { value: DASHBOARD_PERIOD_PRESETS.last90Days, label: '90 dias', days: 90 },
  { value: DASHBOARD_PERIOD_PRESETS.last365Days, label: '365 dias', days: 365 },
  { value: DASHBOARD_PERIOD_PRESETS.all, label: 'Tudo', days: null },
];

// Abre sem corte de data: a tabela mostra as notas mais recentes primeiro.
export const DASHBOARD_DEFAULT_PERIOD = DASHBOARD_PERIOD_PRESETS.all;

export const invoiceStatusTranslation = {
  [invoiceStatus.authorized]: 'Emitida',
  [invoiceStatus.processing]: 'Processando',
  [invoiceStatus.canceled]: 'Cancelada',
  [invoiceStatus.authz_error]: 'Erro',
  [invoiceStatus.replaced]: 'Substituída',
};

// Espelha `NotaTesteStatus` do backend (split_nfse/enums.py). Empresa cadastrada
// antes da trava vem com o status nulo, e a emissão dela não depende de teste.
export const TEST_INVOICE_STATUS = {
  pending: 'pendente',
  processing: 'processando',
  approved: 'aprovada',
  rejected: 'recusada',
};

export const isAwaitingTestInvoice = (status) =>
  Boolean(status) && status !== TEST_INVOICE_STATUS.approved;

// Âncora da seção do certificado na edição do cadastro fiscal. O aviso de
// impedimento de NFS-e aponta para ela pelo hash da URL, então o id é contrato
// entre os dois: mudar aqui muda o destino do aviso junto.
export const FISCAL_EDIT_CERTIFICATE_SECTION_ID = 'certificado-digital';
